import { Component, FormEvent, ReactNode, useEffect, useState } from "react";
import {
  exchangeAuthCode,
  getCurrentAuthState,
  onAuthStateChange,
  signInDemo,
  signInWithPassword,
  signOut,
  updatePassword,
  verifyEmailTokenHash,
  toAuthUser,
  type AuthState
} from "./lib/auth";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { AccountPage } from "./pages/AccountPage";
import { AccessRequestPage } from "./pages/AccessRequestPage";
import { LegalPage } from "./pages/LegalPage";
import { OrderFormPage } from "./pages/OrderFormPage";
import { RegistrationPage } from "./pages/RegistrationPage";
import { DashboardPage } from "./pages/DashboardPage";
import {
  getAccountProfile,
  getRuntimePortalConfig,
  requestRegistrationLink,
  emitPortalTelemetry,
  type AccountProfile,
  type RuntimePortalConfig
} from "./lib/portalApi";
import { appConfig } from "./config/appConfig";
import { PASSWORD_REQUIREMENT_MESSAGE, passwordPolicyError } from "./lib/passwordPolicy";

export type PortalView = "dashboard" | "submit" | "account";

const portalRoutes: Record<PortalView, string> = {
  dashboard: "/dashboard",
  submit: "/submit-prescription",
  account: "/account"
};

function portalViewFromPath(path: string): PortalView | null {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === "/") return "submit";
  if (normalizedPath === "/dashboard") return "dashboard";
  if (normalizedPath === "/submit-prescription" || normalizedPath === "/order-form" || normalizedPath === "/order_form") return "submit";
  if (normalizedPath === "/account") return "account";
  return null;
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

function isRegistrationPath(path: string): boolean {
  const normalizedPath = normalizePath(path);
  return normalizedPath === "/register";
}

function authTokenRouteKind(path: string): "magic" | "recovery" | "invite" | null {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === "/auth/otp" || normalizedPath === "/auth/callback") return "magic";
  if (normalizedPath === "/reset_password" || normalizedPath === "/reset-password") return "recovery";
  if (normalizedPath === "/register") return "invite";
  return null;
}

function canonicalAuthPath(routeKind: "magic" | "recovery" | "invite", fallbackPath: string): string {
  return routeKind === "invite" ? "/register" : fallbackPath;
}

export function App() {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimePortalConfig | null>(null);
  const [isRuntimeConfigLoaded, setIsRuntimeConfigLoaded] = useState(appConfig.environment === "local" || appConfig.useMockApi);
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const [authLinkStatus, setAuthLinkStatus] = useState<"idle" | "verifying" | "verified" | "failed">("idle");
  const [authLinkKind, setAuthLinkKind] = useState<"magic" | "recovery" | "invite" | null>(null);
  const [authLinkExpired, setAuthLinkExpired] = useState(false);
  const [publicPath, setPublicPath] = useState(() => window.location.pathname);
  const [shellUser, setShellUser] = useState<AuthState["user"]>(null);
  const normalizedPublicPath = normalizePath(publicPath);
  const activeView = portalViewFromPath(normalizedPublicPath) ?? "submit";
  const maintenance = runtimeConfig?.maintenance ?? appConfig.maintenance;
  const support = runtimeConfig?.support ?? {
    email: appConfig.support.email,
    phone: appConfig.support.phone,
    phoneHref: `tel:+${appConfig.support.phoneDigits}`,
    technologyEmail: ""
  };

  useEffect(() => {
    let isCurrent = true;
    getRuntimePortalConfig()
      .then((config) => {
        if (!isCurrent) return;
        setRuntimeConfig(config);
      })
      .catch(() => {
        if (!isCurrent) return;
        setRuntimeConfig(null);
      })
      .finally(() => {
        if (isCurrent) setIsRuntimeConfigLoaded(true);
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    getCurrentAuthState()
      .then(setAuthState)
      .catch(() => {
        setAuthState({ session: null, user: null, isDemo: false, isPasswordRecovery: false });
      });

    return onAuthStateChange(setAuthState);
  }, []);

  useEffect(() => {
    function handlePopState() {
      setPublicPath(window.location.pathname);
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!authState?.user) {
      setShellUser(null);
      return;
    }

    const currentUser = authState.user;
    setShellUser(currentUser);
    if (authState.isDemo) return;
    if (isRegistrationPath(normalizedPublicPath)) return;

    let isCurrent = true;
    getAccountProfile()
      .then((profile) => {
        if (!isCurrent) return;
        setShellUser({
          ...currentUser,
          firstName: profile.firstName,
          lastName: profile.lastName
        });
      })
      .catch(() => {
        if (isCurrent) setShellUser(currentUser);
      });

    return () => {
      isCurrent = false;
    };
  }, [authState?.user, authState?.isDemo, normalizedPublicPath]);

  useEffect(() => {
    if (!authState?.user || authState.isPasswordRecovery) return;
    if (normalizedPublicPath === "/privacy-policy" || normalizedPublicPath === "/privacy_policy") return;
    if (normalizedPublicPath === "/terms-of-service" || normalizedPublicPath === "/terms_of_service") return;
    if (isRegistrationPath(normalizedPublicPath)) return;
    if (!portalViewFromPath(normalizedPublicPath)) {
      window.history.replaceState({}, "", portalRoutes.submit);
      setPublicPath(portalRoutes.submit);
    }
  }, [authState?.user, authState?.isPasswordRecovery, normalizedPublicPath]);

  useEffect(() => {
    if (!authState || authState.user) return;
    if (portalViewFromPath(normalizedPublicPath)) {
      window.history.replaceState({}, "", "/login");
      setPublicPath("/login");
    }
  }, [authState, normalizedPublicPath]);

  useEffect(() => {
    const routeKind = authTokenRouteKind(normalizedPublicPath);
    if (!routeKind || authLinkStatus !== "idle") return;

    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get("token_hash");
    const code = params.get("code");
    const type = params.get("type") || (routeKind === "recovery" ? "recovery" : routeKind === "invite" ? "invite" : "email");
    if (!tokenHash && !code) return;

    let isCurrent = true;
    setAuthLinkKind(routeKind);
    setAuthLinkExpired(false);
    setAuthLinkStatus("verifying");
    (async () => {
      try {
        const verifiedSession = tokenHash
          ? await verifyEmailTokenHash(tokenHash, type)
          : code
            ? await exchangeAuthCode(code)
            : null;
        if (!isCurrent) return;
        const nextPath = canonicalAuthPath(routeKind, normalizedPublicPath);
        window.history.replaceState({}, "", nextPath);
        setAuthState({
          session: verifiedSession,
          user: toAuthUser(verifiedSession),
          isDemo: false,
          isPasswordRecovery: routeKind === "recovery"
        });
        setAuthLinkStatus("idle");
        setAuthLinkKind(null);
        setPublicPath(nextPath);
      } catch (error) {
        if (!isCurrent) return;
        const expired = typeof error === "object" && error !== null && "code" in error && error.code === "otp_expired";
        setAuthLinkExpired(expired);
        setAuthLinkStatus("failed");
        emitPortalTelemetry({
          routeTemplate: "/api/auth/verify",
          errorCode: expired ? "auth_link_expired" : "auth_link_verification_failed",
          retryable: !expired
        });
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [normalizedPublicPath]);

  function hydrateShellUser(profile: AccountProfile) {
    setShellUser((current) =>
      current
        ? {
            ...current,
            firstName: profile.firstName,
            lastName: profile.lastName
          }
        : current
    );
  }

  function navigatePublic(path: string) {
    setAuthLinkStatus("idle");
    setAuthLinkKind(null);
    if (normalizePath(path) !== "/login") {
      setAuthError(null);
    }
    window.history.pushState({}, "", path);
    setPublicPath(path);
  }

  function navigatePortal(view: PortalView) {
    navigatePublic(portalRoutes[view]);
  }

  function navigateBack() {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigatePublic("/login");
  }

  async function handleSignIn(email: string, password: string) {
    setAuthError(null);
    setSessionNotice(null);
    try {
      await signInWithPassword(email, password);
      setAuthState(await getCurrentAuthState());
      navigatePublic(portalRoutes.submit);
    } catch {
      setAuthError("We could not sign you in. Check your email and password, then try again.");
    }
  }

  async function handleDemoSignIn() {
    setSessionNotice(null);
    await signInDemo();
    setAuthState(await getCurrentAuthState());
    navigatePublic(portalRoutes.submit);
  }

  async function handleSignOut() {
    setAuthError(null);
    setSessionNotice(null);
    await signOut();
    setAuthState({ session: null, user: null, isDemo: false, isPasswordRecovery: false });
    navigatePublic("/login");
  }

  async function handleSessionTimeout() {
    setAuthError(null);
    setSessionNotice("Your session timed out after a period of inactivity. Please sign in again.");
    try {
      await signOut();
    } catch {
      // Local state is cleared below so the browser cannot keep using the portal after idle timeout.
    }
    setAuthState({ session: null, user: null, isDemo: false, isPasswordRecovery: false });
    navigatePublic("/login");
  }

  if (!isRuntimeConfigLoaded || !authState) {
    return <div className="app-loading">Loading portal...</div>;
  }

  if (maintenance.enabled) {
    return <MaintenanceModePage maintenance={maintenance} support={support} />;
  }

  if (normalizedPublicPath === "/privacy-policy" || normalizedPublicPath === "/privacy_policy") {
    return <LegalPage kind="privacy" onBack={navigateBack} />;
  }

  if (normalizedPublicPath === "/terms-of-service" || normalizedPublicPath === "/terms_of_service") {
    return <LegalPage kind="terms" onBack={navigateBack} />;
  }

  if (authLinkStatus === "verifying") {
    return <AuthLinkStatusView kind={authLinkKind} status="verifying" />;
  }

  if (authLinkStatus === "failed") {
    return (
      <AuthLinkStatusView
        kind={authLinkKind}
        status="failed"
        expired={authLinkExpired}
        onBackToLogin={() => navigatePublic("/login")}
      />
    );
  }

  if (!authState.user) {
    if (normalizedPublicPath === "/access-request") {
      return <AccessRequestPage onBackToLogin={() => navigatePublic("/login")} />;
    }

    if (isRegistrationPath(normalizedPublicPath)) {
      return (
        <LoginPage
          error="Open your approved registration link from the email to finish setup. If you already have an account, sign in below."
          onDemoSignIn={handleDemoSignIn}
          onRegister={() => navigatePublic("/access-request")}
          onSignIn={handleSignIn}
          onClearError={() => setAuthError(null)}
        />
      );
    }

    const initialFlow =
      normalizedPublicPath === "/forgot_password" || normalizedPublicPath === "/forgot-password"
        ? "forgotPassword"
        : normalizedPublicPath === "/auth/otp" || normalizedPublicPath === "/auth/callback"
          ? "magicLink"
          : "signIn";

    return (
      <LoginPage
        error={authError ?? sessionNotice}
        initialFlow={initialFlow}
        onDemoSignIn={handleDemoSignIn}
        onRegister={() => navigatePublic("/access-request")}
        onSignIn={handleSignIn}
        onClearError={() => {
          setAuthError(null);
          setSessionNotice(null);
        }}
      />
    );
  }

  if (authState.isPasswordRecovery) {
    return <PasswordRecoveryView onComplete={() => setAuthState({ ...authState, isPasswordRecovery: false })} />;
  }

  if (isRegistrationPath(normalizedPublicPath)) {
    return (
      <RegistrationPage
        onBackToLogin={handleSignOut}
        onComplete={(profile) => {
          hydrateShellUser(profile);
          navigatePublic(portalRoutes.submit);
        }}
      />
    );
  }

  return (
    <AppShell
      activeView={activeView}
      user={shellUser ?? authState.user}
      isDemo={authState.isDemo}
      onNavigate={navigatePortal}
      onSignOut={handleSignOut}
    >
      <SessionTimeoutGuard onTimeout={handleSessionTimeout} />
      <PortalPageBoundary resetKey={activeView}>
        {activeView === "dashboard" ? <DashboardPage /> : null}
        {activeView === "submit" ? <OrderFormPage /> : null}
        {activeView === "account" ? (
          <AccountPage onAccountDeleted={handleSignOut} onProfileChange={hydrateShellUser} />
        ) : null}
      </PortalPageBoundary>
    </AppShell>
  );
}

function MaintenanceModePage({
  maintenance,
  support
}: {
  maintenance: RuntimePortalConfig["maintenance"];
  support: RuntimePortalConfig["support"];
}) {
  return (
    <main className="maintenance-page" aria-labelledby="maintenance-title">
      {appConfig.showTestBanner ? <div className="test-banner">TEST ENVIRONMENT</div> : null}
      <section className="maintenance-panel" role="status">
        <p className="eyebrow">Companion Pharmacy</p>
        <h1 id="maintenance-title">{maintenance.title || "Under Construction"}</h1>
        <p>
          {maintenance.message ||
            "The Companion Pharmacy Prescriber Portal is temporarily unavailable while our team completes updates. Please check back soon."}
        </p>
        <div className="maintenance-contact">
          <span>Need help?</span>
          <a href={`mailto:${support.email || appConfig.support.email}`}>{support.email || appConfig.support.email}</a>
          <a href={support.phoneHref || `tel:+${appConfig.support.phoneDigits}`}>{support.phone || appConfig.support.phone}</a>
        </div>
      </section>
    </main>
  );
}

function SessionTimeoutGuard({ onTimeout }: { onTimeout: () => void }) {
  const timeoutMs = appConfig.session.idleTimeoutMs;
  const warningMs = Math.min(appConfig.session.warningMs, Math.max(timeoutMs - 1000, 0));
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [deadline, setDeadline] = useState(() => Date.now() + timeoutMs);

  useEffect(() => {
    if (timeoutMs <= 0) return;

    let warningTimer: number | null = null;
    let timeoutTimer: number | null = null;

    function clearTimers() {
      if (warningTimer !== null) window.clearTimeout(warningTimer);
      if (timeoutTimer !== null) window.clearTimeout(timeoutTimer);
    }

    function resetTimers() {
      clearTimers();
      setIsWarningVisible(false);
      setDeadline(Date.now() + timeoutMs);
      warningTimer = window.setTimeout(() => setIsWarningVisible(true), Math.max(timeoutMs - warningMs, 0));
      timeoutTimer = window.setTimeout(() => {
        void onTimeout();
      }, timeoutMs);
    }

    const activityEvents = ["click", "keydown", "pointerdown", "scroll", "touchstart"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetTimers, { passive: true }));
    resetTimers();

    return () => {
      clearTimers();
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetTimers));
    };
  }, [onTimeout, timeoutMs, warningMs]);

  if (timeoutMs <= 0 || !isWarningVisible) return null;

  const minutesRemaining = Math.max(1, Math.ceil((deadline - Date.now()) / 60000));

  return (
    <div className="session-timeout-overlay" role="alertdialog" aria-modal="true" aria-labelledby="session-timeout-title">
      <section className="session-timeout-dialog">
        <p className="eyebrow">Session timeout</p>
        <h2 id="session-timeout-title">Are you still working?</h2>
        <p>
          For security, you will be signed out in about {minutesRemaining} minute{minutesRemaining === 1 ? "" : "s"} unless
          you continue.
        </p>
        <div className="session-timeout-actions">
          <button className="primary-button" type="button" onClick={() => setIsWarningVisible(false)}>
            Stay signed in
          </button>
          <button className="secondary-button" type="button" onClick={() => void onTimeout()}>
            Sign out now
          </button>
        </div>
      </section>
    </div>
  );
}

class PortalPageBoundary extends Component<{ children: ReactNode; resetKey: string }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(previousProps: { resetKey: string }) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <section className="settings-section account-error" role="alert">
          <p className="eyebrow">Portal error</p>
          <h2>Something on this page could not be displayed</h2>
          <p>
            Refresh the page and try again. If this keeps happening, contact support and include the page you were using.
          </p>
          <button className="secondary-button" type="button" onClick={() => window.location.reload()}>
            Refresh page
          </button>
        </section>
      );
    }

    return this.props.children;
  }
}

function AuthLinkStatusView({
  kind,
  status,
  expired = false,
  onBackToLogin
}: {
  kind: "magic" | "recovery" | "invite" | null;
  status: "verifying" | "failed";
  expired?: boolean;
  onBackToLogin?: () => void;
}) {
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [requestState, setRequestState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [requestMessage, setRequestMessage] = useState<string | null>(null);
  const label =
    kind === "recovery"
      ? "password reset link"
      : kind === "invite"
        ? "registration link"
        : "sign-in link";
  const failedTitle = kind === "invite" && expired ? "Registration link expired or already used" : "Link could not be verified";
  const failedMessage =
    kind === "invite" && expired
      ? "This registration link can no longer be used. If your account is active, sign in below. If you still need to finish registration, request another registration link and our team will review your account."
      : `We could not verify your ${label}. This does not necessarily mean it expired. Please try opening it again. If the problem continues, contact support.`;

  async function handleRegistrationLinkRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRequestMessage(null);
    const email = registrationEmail.trim().toLowerCase();
    if (!email) {
      setRequestState("failed");
      setRequestMessage("Enter the email address used for your registration.");
      return;
    }
    setRequestState("sending");
    try {
      const result = await requestRegistrationLink({ email });
      setRequestState("sent");
      setRequestMessage(result.message);
    } catch {
      setRequestState("failed");
      setRequestMessage("We could not send the request right now. Please try again or contact support.");
    }
  }

  return (
    <main className="login-page">
      {import.meta.env.VITE_APP_ENV !== "prod" ? <div className="test-banner">TEST ENVIRONMENT</div> : null}
      <section className="login-brand" aria-label="Companion Pharmacy Prescriber Portal">
        <img
          src="https://vp-brand-assets.s3.amazonaws.com/companion-pharmacy/companion_pharmacy_logo_primary.png"
          alt="Companion Pharmacy"
        />
        <h1>Prescriber Portal</h1>
        <p>Submit and manage prescriptions with a lighter, cleaner workflow.</p>
      </section>
      <section className="login-panel" aria-live="polite">
        <h2>{status === "verifying" ? "Verifying link" : failedTitle}</h2>
        <p>
          {status === "verifying"
            ? `One moment while we verify your ${label}.`
            : failedMessage}
        </p>
        {status === "failed" ? (
          <div className="auth-link-actions">
            {kind === "invite" ? (
              <form className="auth-link-request-form" onSubmit={handleRegistrationLinkRequest}>
                <label htmlFor="registration-link-email">Email address</label>
                <input
                  id="registration-link-email"
                  type="email"
                  autoComplete="email"
                  value={registrationEmail}
                  onChange={(event) => setRegistrationEmail(event.target.value)}
                  disabled={requestState === "sending" || requestState === "sent"}
                />
                {requestMessage ? (
                  <p className={requestState === "sent" ? "form-success" : "form-error"}>{requestMessage}</p>
                ) : null}
                <button className="primary-button" type="submit" disabled={requestState === "sending" || requestState === "sent"}>
                  {requestState === "sending" ? "Sending request..." : "Request another registration link"}
                </button>
              </form>
            ) : null}
            {onBackToLogin ? (
              <button className="secondary-button" type="button" onClick={onBackToLogin}>
                Back to sign in
              </button>
            ) : null}
          </div>
        ) : null}
      </section>
    </main>
  );
}

function PasswordRecoveryView({ onComplete }: { onComplete: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const passwordError = passwordPolicyError(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSaving(true);
    try {
      await updatePassword(password);
      onComplete();
    } catch {
      setError("Password could not be updated. Please request a new reset link and try again.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <main className="login-page">
      {import.meta.env.VITE_APP_ENV !== "prod" ? <div className="test-banner">TEST ENVIRONMENT</div> : null}
      <section className="login-brand" aria-label="Companion Pharmacy Prescriber Portal">
        <img
          src="https://vp-brand-assets.s3.amazonaws.com/companion-pharmacy/companion_pharmacy_logo_primary.png"
          alt="Companion Pharmacy"
        />
        <h1>Reset password</h1>
        <p>Choose a new password for your Prescriber Portal account.</p>
      </section>
      <section className="login-panel" aria-labelledby="password-recovery-heading">
        <h2 id="password-recovery-heading">Create a new password</h2>
        <form onSubmit={handleSubmit}>
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <span className="field-help">
              {PASSWORD_REQUIREMENT_MESSAGE}
            </span>
          </label>
          <label>
            Confirm password
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
          </label>
          {error ? <div className="form-alert">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save password"}
          </button>
        </form>
      </section>
    </main>
  );
}
